import { Component } from "@angular/core";
import { ActivatedRoute,Router } from "@angular/router";
import { Department } from "@enums/department";
import { OpsPlanningMode } from "@enums/opsPlanningMode.enum";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { Shift } from "@enums/shift.enum";
import { environment } from "@environments/environment";
import { GetProductionStatusColor } from "@helpers/getProductionStatusColor";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { ChangeWorkOrderStageDesiredStartTime } from "@interfaces/changeWorkOrderStageDesiredStartTime.interface";
import { ChangeWorkOrderStageEstimatedStartDate } from "@interfaces/changeWorkOrderStageEstimatedStartDate.interface";
import { ChangeWorkOrderStageMachine } from "@interfaces/changeWorkOrderStageMachine.interface";
import { ChangeWorkOrderStageOperator } from "@interfaces/changeWorkOrderStageOperator.interface";
import { ChangeWorkOrderStagePriority } from "@interfaces/changeWorkOrderStagePriority.interface";
import { Site } from "@interfaces/site.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { MachineService } from "@services/machine.service";
import { SiteService } from "@services/site.service";
import { TrainingService } from "@services/trainingService.service";
import { UserProfileService } from "@services/userProfile.service";
import { WorkOrderStageService } from "@services/workOrderStage.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "sites-id",
	styleUrls: ["sites-id.css"],
	templateUrl: "sites-id.html"
})
export class OpsPlanningSitesIdAdminComponent {
	changes: any = [];
	departments: any;
	dropdownUserProfiles: UserProfile[] = [];
	editRowKey: any = null;
	headerPrimaryButtons: AdminHeaderButton[] = [];
	headerSecondaryButtons: AdminHeaderButton[] = [];
	headerTertiaryButtons: AdminHeaderButton[] = [];
	machineTrainingRequiredForAssignment = true;
	machines: any[] = [];
	machinesShowingAllAvailable: number[] = [];
	machinesShowingAllPending: number[] = [];
	machinesShowingAllRunning: number[] = [];
	minDate: Date = new Date();
	opsPlanningDate: Date = new Date();
	opsPlanningMode: OpsPlanningMode = OpsPlanningMode.ForDate;
	opsPlanningModes: any;
	popupAddButtonOptions: any;
	popupChangeMachineProductionStatus: { machineId: number, productionStatus: ProductionStatus } = { machineId: 0, productionStatus: ProductionStatus.Active };
	popupChangeWorkOrderStageDesiredStartTime: ChangeWorkOrderStageDesiredStartTime = new ChangeWorkOrderStageDesiredStartTime();
	popupChangeWorkOrderStageEstimatedStartDate: ChangeWorkOrderStageEstimatedStartDate = new ChangeWorkOrderStageEstimatedStartDate();
	popupChangeWorkOrderStageMachine: ChangeWorkOrderStageMachine = new ChangeWorkOrderStageMachine();
	popupChangeWorkOrderStageOperator: ChangeWorkOrderStageOperator = new ChangeWorkOrderStageOperator();
	popupCloseButtonOptions: any = { onClick: () => this.closePopup(), text: "Close" };
	popupEstimatedShifts = 0;
	popupOperatorQueue: any[] = [];
	popupTitle = "Change Machine";
	popupVisible = false;
	priorities: number[];
	productionStatuses: any;
	shifts: any;
	siteId = 0;
	sites: Site[] = [];
	title = "Ops Planning";
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private machineService: MachineService, private siteService: SiteService, private trainingService: TrainingService, private userProfileService: UserProfileService, private workOrderStageService: WorkOrderStageService) {
		this.departments = GetPropertiesFromEnum(Department);
		this.opsPlanningModes = GetPropertiesFromEnum(OpsPlanningMode);
		this.productionStatuses = GetPropertiesFromEnum(ProductionStatus);
		this.shifts = GetPropertiesFromEnum(Shift);
		this.route.params
			.subscribe((params) => {
				this.siteId = params.site;
				this.getMachines();
				this.getSites();
				this.getUserProfiles();
			});
		this.getTrainingRequired();
		this.changeWorkOrderStageMachinePopup = this.changeWorkOrderStageMachinePopup.bind(this);
		this.changeOpsPlanningDate = this.changeOpsPlanningDate.bind(this);
		this.changeOpsPlanningMode = this.changeOpsPlanningMode.bind(this);
		this.recalculateStageEstimates = this.recalculateStageEstimates.bind(this);
		this.showRunningSections = this.showRunningSections.bind(this);
		this.updatePopupOperatorQueueOrder = this.updatePopupOperatorQueueOrder.bind(this);
		this.viewToday = this.viewToday.bind(this);

		this.priorities = [];
		for (let i = 1; i <= environment.build.lowestWorkOrderStagePriority; i++) {
			this.priorities.push(i);
		}
	}

	assignNextOperatorInQueue(id: number) {
		if (confirm("Are you sure you wish to assign the next operator in the queue to this stage? This cannot be undone without manually resetting the assignment and queue.") == false) {
			return false;
		}
		this.workOrderStageService.nextOperator(id)
			.subscribe(
				() => {
					this.toastService.success("Work Order Operator assigned from the Queue");
					this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	buildButtons() {
		this.headerTertiaryButtons = [
			{ icon: "fal fa-sync-alt", method: "getMachines", text: "" },
			{ method: "goToEdiOrders", text: "Firewall" },
			{ method: "goToWorkforce", text: "Workforce" }
		];

		this.headerSecondaryButtons = [];

		const filteredDepartments = this.departments.filter((d: { id: number; name: string; }) => (d.id > 0 && d.id < 55) || d.id == 70);
		filteredDepartments.forEach((fd: any) => {
			this.headerSecondaryButtons.push({ method: "goToDepartment-" + fd.id, text: fd.name });
		});

		this.headerPrimaryButtons = [];

		this.sites.forEach((site: any) => {
			this.headerPrimaryButtons.push({ method: "goToSite-" + site.id, text: site.name });
		});

		const selectedSite = this.sites.filter((d: { id: number; name: string; }) => d.id == this.siteId);
		this.title = selectedSite[0].name;
	}

	changeMachineProductionStatus() {
		this.machineService.updateProductionStatusSupervisor(this.popupChangeMachineProductionStatus.machineId, this.popupChangeMachineProductionStatus.productionStatus)
			.subscribe(
				() => {
					this.toastService.success("Machine Production Status Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeMachineProductionStatusPopup(machineId: number) {
		const selectedMachine = this.machines.filter((m: any) => m.machineId === machineId);
		this.popupChangeMachineProductionStatus.machineId = machineId;
		this.popupChangeMachineProductionStatus.productionStatus = selectedMachine[0].productionStatus;
		this.popupAddButtonOptions = { onClick: () => this.changeMachineProductionStatus(), text: "Change Production Status" };
		this.popupTitle = "Change Production Status";
		this.popupVisible = true;
		return false;
	}

	changeOpsPlanningDate(e: any) {
		this.opsPlanningDate = e.value;
		this.getMachines();
	}

	changeOpsPlanningMode(e: any) {
		this.opsPlanningMode = e.value;
		this.getMachines();
	}

	changeWorkOrderStageDesiredStartTime() {
		this.workOrderStageService.changeDesiredStartTime(this.popupChangeWorkOrderStageDesiredStartTime)
			.subscribe(
				() => {
					this.toastService.success("Work Order Stage Start Time Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageDesiredStartTimePopup(stageId: number) {
		this.popupChangeWorkOrderStageDesiredStartTime = new ChangeWorkOrderStageDesiredStartTime();
		this.popupChangeWorkOrderStageDesiredStartTime.workOrderStageId = stageId;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageDesiredStartTime(), text: "Change Time" };
		this.popupTitle = "Change Desired Start Time";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageEstimatedStartDate() {
		this.workOrderStageService.changeEstimatedStartDate(this.popupChangeWorkOrderStageEstimatedStartDate)
			.subscribe(
				() => {
					this.toastService.success("Work Order Stage Estimated Start Date Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageEstimatedStartDatePopup(stageId: number) {
		this.popupChangeWorkOrderStageEstimatedStartDate = new ChangeWorkOrderStageEstimatedStartDate();
		this.popupChangeWorkOrderStageEstimatedStartDate.workOrderStageId = stageId;
		this.popupChangeWorkOrderStageEstimatedStartDate.estimatedStartDate = this.opsPlanningDate;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageEstimatedStartDate(), text: "Change Date" };
		this.popupTitle = "Change Estimated Start Date";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageMachine() {
		this.workOrderStageService.changeMachine(this.popupChangeWorkOrderStageMachine)
			.subscribe(
				() => {
					this.toastService.success("Work Order Stage Machine Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageMachinePopup(stageId: number) {
		this.popupChangeWorkOrderStageMachine = new ChangeWorkOrderStageMachine();
		this.popupChangeWorkOrderStageMachine.workOrderStageId = stageId;
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageMachine(), text: "Change Machine" };
		this.popupTitle = "Change Machine";
		this.popupVisible = true;
		return false;
	}

	changeWorkOrderStageOperator() {
		this.popupChangeWorkOrderStageOperator.operatorQueue = JSON.stringify(this.popupOperatorQueue);
		this.workOrderStageService.changeOperator(this.popupChangeWorkOrderStageOperator)
			.subscribe(
				() => {
					this.toastService.success("Work Order Stage Operators Updated");
					this.getMachines();
					this.closePopup();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
	}

	changeWorkOrderStageOperatorPopup(stageId: number, machineId: number, isRunningStage: boolean) {
		if (machineId === undefined || machineId <= 0) {
			alert("Please assign this stage to a machine first.");
			return false;
		}

		const selectedMachine = this.machines.filter((m: any) => m.machineId === machineId)[0];
		const selectedWorkOrderStage = isRunningStage ? selectedMachine.runningStages.filter((s: any) => s.id === stageId)[0] : selectedMachine.availableStages.filter((s: any) => s.id === stageId)[0];

		// Pull details from the machines
		this.popupChangeWorkOrderStageOperator = new ChangeWorkOrderStageOperator();
		this.popupChangeWorkOrderStageOperator.workOrderStageId = stageId;
		this.popupChangeWorkOrderStageOperator.userProfileId = selectedWorkOrderStage.assignedUserProfileId;
		this.popupChangeWorkOrderStageOperator.operatorQueue = selectedWorkOrderStage.operatorQueue;
		this.popupEstimatedShifts = selectedWorkOrderStage.estimatedShifts;
		this.popupOperatorQueue = JSON.parse(selectedWorkOrderStage.operatorQueue);
		this.popupAddButtonOptions = { onClick: () => this.changeWorkOrderStageOperator(), text: "Change Operators" };

		if (this.machineTrainingRequiredForAssignment === false) {
			this.userProfileService.byMachine(machineId)
				.subscribe(
					(res: any) => {
						this.dropdownUserProfiles = res.response;
						this.dropdownUserProfiles.sort(function (a, b) {
							return a.shiftId as number - b.shiftId as number || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
						});
						this.popupTitle = "Change Operators";
						this.popupVisible = true;
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					}
				);
		} else {
			this.trainingService.usersTrainedForMachine(machineId)
				.subscribe(
					(res: any) => {
						this.dropdownUserProfiles = res.response;
						this.dropdownUserProfiles.sort(function (a, b) {
							return a.shiftId as number - b.shiftId as number || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
						});
						this.popupTitle = "Change Operators";
						this.popupVisible = true;
					},
					(err) => {
						console.log(err);
						this.toastService.error("Something Went Wrong!");
					}
				);
		}
		
		return false;
	}

	changeWorkOrderStagePriority(stageId: number, priority: number) {
		const data = new ChangeWorkOrderStagePriority();
		data.priority = priority;
		data.workOrderStageId = stageId;
		this.workOrderStageService.changePriority(data)
			.subscribe(
				() => {
					this.toastService.success("Priority Changed - Refresh page to update details and order.");
					// this.getMachines();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				},
			);
		return false;
	}

	changeWorkOrderStagePriorityDropDown(args: any, stageId: number) {
		const newPriority = args.value;

		this.changeWorkOrderStagePriority(stageId, newPriority);
	}

	closePopup() {
		this.popupVisible = false;
	}

	displayMachineInDropdown(e: any) { return e === null ? null : e.cellNumber === null ? e.machineName : `${e.cellNumber} - ${e.machineName}`; }

	displayUserProfileInDropdown(e: any) { 
		return e === null ? null : `${e.firstName} ${e.lastName} - ${(e.shiftId === Shift.Day ? "Day" : "Night")} Shift`;
	}

	getMachines() {		
		this.machines = [];

		switch (this.opsPlanningMode) {
			case OpsPlanningMode.All:				
				this.machineService.opsPlannerForSite(this.siteId)
					.subscribe(
						(res: any) => this.machines = res.response,
						(err) => {
							console.log(err);
							this.toastService.error("Something Went Wrong!");
						}
					);
				break;
			case OpsPlanningMode.ForDate:				
				this.machineService.opsPlannerForSiteAndDate(this.siteId, this.opsPlanningDate)
					.subscribe(
						(res: any) => this.machines = res.response,
						(err) => {
							console.log(err);
							this.toastService.error("Something Went Wrong!");
						}
					);
				break;
			case OpsPlanningMode.Unestimated:		
				this.machineService.opsPlannerUnestimatedForSite(this.siteId)
					.subscribe(
						(res: any) => this.machines = res.response,
						(err) => {
							console.log(err);
							this.toastService.error("Something Went Wrong!");
						}
					);
				break;
		}
	}

	getSites() {		
		this.siteService.getAll()
			.subscribe(
				(res: any) => {
					this.sites = res.response.items;
					this.buildButtons();
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getTrainingRequired() {
		this.workOrderStageService.machineTrainingRequiredForAssignment()
			.subscribe(
				(res: any) => {
					this.machineTrainingRequiredForAssignment = res.response;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getUserProfiles() {		
		this.userProfileService.getActiveUsers()
			.subscribe(
				(res: any) => {
					this.userProfiles = res.response;
					this.userProfiles.sort(function (a, b) {
						return a.shiftId as number - b.shiftId as number || a.firstName.localeCompare(b.firstName) || a.lastName.localeCompare(b.lastName);
					});

					this.dropdownUserProfiles = this.userProfiles;
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	goToDepartment(department: number) {
		this.router.navigate([`/admin/opsPlanning/department/${department}`]);
	}

	goToEdiOrders() {
		this.router.navigate(["/admin/opsPlanning/ediOrders"]);
	}

	goToSite(site: number) {
		this.router.navigate([`/admin/opsPlanning/site/${site}`]);
	}

	goToWorkforce() {
		this.router.navigate(["/admin/opsPlanning/workforce"]);
	}

	headerButtonClick(method: string) {
		if (method.indexOf("-") > 0) {
			const split = method.split("-");
			// @ts-ignore // Required to be able to call the method directly from the variable
			this[split[0]](split[1]);
		}
		else
		{
			// @ts-ignore // Required to be able to call the method directly from the variable
			if (this[method]) this[method]();
		}
	}

	recalculateStageEstimates(stageId: number) {
		this.workOrderStageService.recalculateEstimates(stageId)
			.subscribe(
				() => {
					this.getMachines();
					this.toastService.success("Estimates Reworked, reloading details.");
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
		return false;
	}

	renderDateString(date: Date)
	{
		const dateToChange = new Date(date);
		return dateToChange.toLocaleDateString("en-GB");
	}

	renderStatusColor(status:ProductionStatus){
		return GetProductionStatusColor(status);
	}

	showRunningSections() {
		return this.opsPlanningMode === OpsPlanningMode.ForDate && this.opsPlanningDate.toDateString() === this.minDate.toDateString();
	}

	toggleMachineShowingAllAvailable(machineId: number) {
		const currentIndex = this.machinesShowingAllAvailable.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllAvailable.splice(currentIndex);
		} else {
			this.machinesShowingAllAvailable.push(machineId);
		}
		return false;
	}

	toggleMachineShowingAllPending(machineId: number) {
		const currentIndex = this.machinesShowingAllPending.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllPending.splice(currentIndex);
		} else {
			this.machinesShowingAllPending.push(machineId);
		}
		return false;
	}

	toggleMachineShowingAllRunning(machineId: number) {
		const currentIndex = this.machinesShowingAllRunning.indexOf(machineId);
		if (currentIndex >= 0) {
			this.machinesShowingAllRunning.splice(currentIndex);
		} else {
			this.machinesShowingAllRunning.push(machineId);
		}
		return false;
	}

	updatePopupOperatorQueueOrder(e: any) {
		const visibleRows = e.component.getVisibleRows();
		const toIndex = this.popupOperatorQueue.indexOf(visibleRows[e.toIndex].data);
		const fromIndex = this.popupOperatorQueue.indexOf(e.itemData);
	
		this.popupOperatorQueue.splice(fromIndex, 1);
		this.popupOperatorQueue.splice(toIndex, 0, e.itemData);
	}

	viewToday() {
		this.opsPlanningDate = new Date();
		this.opsPlanningMode = OpsPlanningMode.ForDate;
	}
}